import React, { useState, useEffect } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import DatePicker from "react-datepicker";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-datepicker/dist/react-datepicker.css";
import { format, parse, startOfWeek, getDay } from "date-fns";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  addDoc,
  query
} from "firebase/firestore";
import { db, auth } from "../firebase";
import Select from "react-select";
import PageHeader from "./PageHeader";

const locales = { "en-US": require("date-fns/locale/en-US") };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const CalendarPage = () => {
  const [rosters, setRosters] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading] = useState(true);
  const [uid, setUid] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [time, setTime] = useState(new Date());
  const [endTime, setEndTime] = useState(new Date());
  const [place, setPlace] = useState("");
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setUid(user.uid);
        fetchRosters(user.uid);
        fetchEvents(user.uid);
      }
    });
    return () => unsubscribe();
  }, []);

  const fetchRosters = async (userId) => {
    try {
      const rostersRef = collection(db, "rosters");
      const querySnapshot = await getDocs(query(rostersRef));
      const fetchedRosters = [];

      for (const rosterDoc of querySnapshot.docs) {
        const rosterData = { id: rosterDoc.id, ...rosterDoc.data() };

        if (rosterData.userId === userId) {
          const peopleRef = collection(
            db,
            "users",
            userId,
            "rosters",
            rosterDoc.id,
            "people"
          );
          const peopleSnapshot = await getDocs(peopleRef);

          const people = peopleSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));

          fetchedRosters.push({ ...rosterData, people });
        }
      }

      setRosters(fetchedRosters);
      // console.log("Fetched rosters with people:", fetchedRosters);
    } catch (error) {
      console.error("Error fetching rosters:", error);
    }
  };
  

  const fetchEvents = async (userId) => {
    try {
      const eventsRef = collection(db, "events");
      const querySnapshot = await getDocs(eventsRef);
      const eventsData = querySnapshot.docs
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
          start: doc.data().start.toDate(),
          end: doc.data().end.toDate(),
        }))
        .filter((event) => event.userId === userId);
      setEvents(eventsData);
    } catch (err) {
      console.error("Error fetching events:", err);
    }
  };

  const updateUpcomingEventForPerson = async (personId, upcomingEvent) => {
    try {
      console.log(`Attempting to update upcomingEvent for person: ${personId}`);
  
      // Find the person's roster from the local rosters state
      const personRoster = rosters.find((roster) =>
        roster.people.some((person) => person.id === personId)
      );
  
      if (!personRoster) {
        console.error(`Person not found in any roster: ${personId}`);
        return;
      }
  
      const { id: rosterId } = personRoster;
  
      // Access the person's document in Firestore
      const personDocRef = doc(
        db,
        "users",
        auth.currentUser.uid,
        "rosters",
        rosterId,
        "people",
        personId
      );
  
      await updateDoc(personDocRef, { upcomingEvent });
  
      console.log(
        `Successfully updated upcomingEvent for person: ${personId} to: ${upcomingEvent}`
      );
    } catch (err) {
      console.error("Error updating upcomingEvent:", err);
    }
  };
  
  
  

  const handleAddOrUpdateEvent = async () => {
    if (!selectedPerson || !time || !endTime || !place || !selectedDate) {
      alert("Please fill in all fields.");
      return;
    }

    const newEvent = {
      title: `${selectedPerson.label} - ${place}`,
      start: new Date(selectedDate.setHours(time.getHours(), time.getMinutes())),
      end: new Date(selectedDate.setHours(endTime.getHours(), endTime.getMinutes())),
      place,
      userId: uid,
      personId: selectedPerson.value,
    };

    try {
      if (!selectedEvent) {
        const eventsRef = collection(db, "events");
        const docRef = await addDoc(eventsRef, newEvent);
        newEvent.id = docRef.id;
        setEvents((prev) => [...prev, newEvent]);
      } else {
        const eventDocRef = doc(db, "events", selectedEvent.id);
        await updateDoc(eventDocRef, newEvent);
        setEvents((prev) =>
          prev.map((event) =>
            event.id === selectedEvent.id ? { ...newEvent, id: selectedEvent.id } : event
          )
        );
      }

      await updateUpcomingEventForPerson(
        selectedPerson.value,
        `You have a date with ${selectedPerson.label} on ${format(
          newEvent.start,
          "MMMM d, yyyy 'at' h:mm a"
        )} to ${format(newEvent.end, "h:mm a")} at ${place}`
      );
      setShowModal(false);
    } catch (err) {
      console.error("Error saving event:", err);
    }
  };

  const handleDeleteEvent = async () => {
    if (!selectedEvent) return;

    try {
      const eventDocRef = doc(db, "events", selectedEvent.id);
      await deleteDoc(eventDocRef);
      setEvents((prev) => prev.filter((event) => event.id !== selectedEvent.id));
      await updateUpcomingEventForPerson(selectedEvent.personId, "");
      setShowModal(false);
    } catch (err) {
      console.error("Error deleting event:", err);
    }
  };

  const peopleOptions = rosters.flatMap((roster) =>
    (roster.people || []).map((person) => ({
      value: person.id,
      label: person.name,
    }))
  );

  return (
    <div>
      <PageHeader title="Calendar" />
      <Calendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        style={{ height: "75vh", margin: "20px" }}
        selectable
        onSelectSlot={(slotInfo) => {
          setSelectedDate(slotInfo.start);
          setShowModal(true);
          setSelectedEvent(null);
          setTime(slotInfo.start);
          setEndTime(slotInfo.end);
          setPlace("");
          setSelectedPerson(null);
        }}
        onSelectEvent={(event) => {
          setSelectedEvent(event);
          setShowModal(true);
          setSelectedDate(event.start);
          setTime(event.start);
          setEndTime(event.end);
          setPlace(event.place);

          const foundPerson = rosters
            .flatMap((roster) => roster.people || [])
            .find((p) => p.id === event.personId);

          setSelectedPerson(
            foundPerson ? { value: foundPerson.id, label: foundPerson.name } : null
          );
        }}
      />
      {showModal && (
        <div className="modal">
          <div className="modal-content">
            <button
              className="close-button"
              onClick={() => setShowModal(false)}
            >
              &times;
            </button>
            <h2>{selectedEvent ? "Edit Event" : "Add Event"}</h2>
            <label>
              Person:
              <Select
                options={peopleOptions}
                onChange={(selected) => setSelectedPerson(selected)}
                value={selectedPerson}
              />
            </label>
            <label>
              Start Time:
              <DatePicker
                selected={time}
                onChange={(date) => setTime(date)}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={15}
                timeCaption="Start Time"
                dateFormat="h:mm aa"
              />
            </label>
            <label>
              End Time:
              <DatePicker
                selected={endTime}
                onChange={(date) => setEndTime(date)}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={15}
                timeCaption="End Time"
                dateFormat="h:mm aa"
              />
            </label>
            <label>
              Place:
              <input
                type="text"
                value={place}
                onChange={(e) => setPlace(e.target.value)}
              />
            </label>
            <button onClick={handleAddOrUpdateEvent}>
              {selectedEvent ? "Save Changes" : "Add Event"}
            </button>
            {selectedEvent && (
              <button onClick={handleDeleteEvent} className="delete-button">
                Delete Event
              </button>
            )}
          </div>
        </div>
      )}
      {loading && <p></p>}
    </div>
  );
};

export default CalendarPage;
